import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ArrowLeft, Building2, Users, GraduationCap, Clock, BookOpen,
  MapPin, Shield, LogIn, ChevronDown, Search, UserCheck, X,
  Mail, Phone, Tag,
} from 'lucide-react';
import { useImpersonate } from '../../hooks/useImpersonate';
import { toast } from 'sonner';

const API = import.meta.env.VITE_API_URL || '/api/v1';
const getToken = () => localStorage.getItem('ac_token');
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

function useEscolaDetalhe(id: string) {
  return useQuery({
    queryKey: ['escola-detalhe', id],
    queryFn: async () => {
      const res = await fetch(`${API}/escolas/${id}`, { headers: headers() });
      if (!res.ok) throw new Error('Falha');
      return res.json();
    },
    enabled: !!id,
  });
}

function useEscolaEquipe(id: string) {
  return useQuery({
    queryKey: ['escola-equipe', id],
    queryFn: async () => {
      const res = await fetch(`${API}/admin/usuarios`, { headers: headers() });
      if (!res.ok) throw new Error('Falha');
      const all = await res.json();
      const list = Array.isArray(all) ? all : all.data || [];
      return list.filter((u: any) => u.escolaId === id);
    },
    enabled: !!id,
  });
}

function useEscolaTurmas(id: string) {
  return useQuery({
    queryKey: ['escola-turmas', id],
    queryFn: async () => {
      const res = await fetch(`${API}/escolas/${id}/turmas`, { headers: headers() });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!id,
  });
}

function useEscolaMaterias(id: string) {
  return useQuery({
    queryKey: ['escola-materias', id],
    queryFn: async () => {
      const res = await fetch(`${API}/escolas/${id}/materias`, { headers: headers() });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!id,
  });
}

function useAlunosTurma(escolaId: string, turmaId: string | null) {
  return useQuery({
    queryKey: ['alunos-turma', escolaId, turmaId],
    queryFn: async () => {
      const res = await fetch(`${API}/escolas/${escolaId}/alunos/turma/${turmaId}`, { headers: headers() });
      if (!res.ok) return { totalAlunos: 0, alunos: [] };
      return res.json();
    },
    enabled: !!escolaId && !!turmaId,
  });
}

// ─── Alunos Dialog ────────────────────────────────────────────────────────────

function AlunosDialog({ turma, escolaId, onClose, onImpersonate, impersonating, onSelectAluno }: {
  turma: any; escolaId: string; onClose: () => void;
  onImpersonate: (uid: string) => void; impersonating: string | null;
  onSelectAluno?: (aluno: any) => void;
}) {
  const { data, isLoading } = useAlunosTurma(escolaId, turma.id);
  const [busca, setBusca] = useState('');

  const alunos: any[] = data?.alunos || [];
  const filtered = busca.trim()
    ? alunos.filter((a: any) => a.nome.toLowerCase().includes(busca.toLowerCase()))
    : alunos;

  const profList = (turma.professores || []).map((pt: any) => `${pt.professor?.nome?.split(' ')[0] || '?'} (${pt.materia?.nome || '?'})`);
  const uniqueProfs = Array.from(new Set(profList as string[]));

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'fadeIn .2s ease',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 'min(95vw, 800px)', height: '90vh', background: 'var(--bg-card, white)',
        borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        border: '1px solid var(--border-color, #e5e7eb)',
        boxShadow: '0 25px 50px rgba(0,0,0,.25)',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--border-color, #e5e7eb)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text-main)' }}>
              {turma.nome} — {turma.serie}
            </div>
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
              {turma.turnoOperacional?.nome || turma.turno || ''} • {turma._count?.alunos || alunos.length} alunos
            </div>
            {uniqueProfs.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                {uniqueProfs.map((p, i) => (
                  <span key={i} style={{
                    fontSize: '.62rem', padding: '2px 7px', borderRadius: 5,
                    background: '#3b82f610', color: '#3b82f6', fontWeight: 500,
                  }}>{p}</span>
                ))}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{
            width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--bg-main, #f3f4f6)', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
          }}>
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '10px 20px', flexShrink: 0 }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="text" placeholder="Buscar aluno por nome..."
              value={busca} onChange={(e) => setBusca(e.target.value)}
              autoFocus
              style={{
                width: '100%', padding: '8px 32px 8px 32px', borderRadius: 10,
                border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-main, #f8fafc)',
                fontSize: '.82rem', color: 'var(--text-main)', outline: 'none',
              }}
            />
            {busca && (
              <button onClick={() => setBusca('')} style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%',
                width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontSize: '.65rem', fontWeight: 700,
              }} title="Limpar busca">✕</button>
            )}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 20px' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: '.82rem' }}>Carregando alunos...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: '.82rem', fontStyle: 'italic' }}>
              {busca ? 'Nenhum aluno encontrado' : 'Nenhum aluno nesta turma'}
            </div>
          ) : filtered.map((al: any) => (
            <div key={al.id} onClick={() => onSelectAluno?.(al)} style={{
              padding: '10px 14px', borderRadius: 10, marginBottom: 6,
              background: 'var(--bg-main, #f8fafc)', border: '1px solid var(--border-color, #e5e7eb)',
              cursor: 'pointer', transition: 'all .15s',
            }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = '#f97316'; (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(249,115,22,.12)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = ''; (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                    background: al.fotoUrl ? `url(${al.fotoUrl}) center/cover` : 'linear-gradient(135deg, #f97316, #fb923c)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontWeight: 700, fontSize: '.7rem',
                  }}>
                    {!al.fotoUrl && (al.nome?.charAt(0)?.toUpperCase() || '?')}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '.82rem', color: 'var(--text-main)' }}>{al.nome}</div>
                    {al.matricula && <div style={{ fontSize: '.66rem', color: 'var(--text-muted)' }}>Mat: {al.matricula}</div>}
                  </div>
                </div>
              </div>
              {/* Responsaveis */}
              {(al.responsaveis || []).length > 0 && (
                <div style={{ marginTop: 6, paddingLeft: 10, borderLeft: '2px solid var(--border-color, #e5e7eb)' }}>
                  {al.responsaveis.map((ra: any) => {
                    const r = ra.responsavel;
                    const temConta = !!r?.usuarioId;
                    const ativo = r?.usuario?.ativo;
                    return (
                      <div key={r?.id || Math.random()} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '4px 8px', borderRadius: 6, marginBottom: 3,
                        background: 'var(--bg-card, white)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                          <UserCheck size={12} style={{ color: temConta ? '#8b5cf6' : '#9ca3af', flexShrink: 0 }} />
                          <div style={{ minWidth: 0 }}>
                            <span style={{ fontWeight: 500, fontSize: '.74rem', color: 'var(--text-main)' }}>{r?.nome}</span>
                            {ra.grauParentesco && <span style={{ fontSize: '.62rem', color: 'var(--text-muted)', marginLeft: 4 }}>({ra.grauParentesco})</span>}
                            {ra.principal && <span style={{ marginLeft: 4, padding: '0 4px', borderRadius: 3, fontSize: '.58rem', fontWeight: 700, background: '#f59e0b20', color: '#f59e0b' }}>Principal</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                          {temConta ? (
                            <>
                              <span style={{ fontSize: '.58rem', padding: '1px 6px', borderRadius: 4, background: ativo ? '#10b98115' : '#ef444415', color: ativo ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                                {ativo ? 'Ativo' : 'Inativo'}
                              </span>
                              {ativo && (
                                <button onClick={() => onImpersonate(r.usuarioId)} disabled={impersonating === r.usuarioId} style={{
                                  padding: '2px 7px', fontSize: '.6rem', fontWeight: 600, borderRadius: 4,
                                  background: '#6366f115', color: '#6366f1', border: 'none', cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', gap: 3,
                                }}>
                                  <LogIn size={9} /> Entrar
                                </button>
                              )}
                            </>
                          ) : (
                            <span style={{ fontSize: '.58rem', padding: '1px 6px', borderRadius: 4, background: '#9ca3af15', color: '#9ca3af', fontWeight: 600 }}>Sem conta</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {(!al.responsaveis || al.responsaveis.length === 0) && (
                <div style={{ marginTop: 4, paddingLeft: 10, fontSize: '.68rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Nenhum responsavel vinculado</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── UserProfileDialog ────────────────────────────────────────────────────────

const PROFILE_COLORS: Record<string, { bg: string; accent: string }> = {
  ESCOLA: { bg: 'linear-gradient(135deg, #10b981, #059669)', accent: '#10b981' },
  PROFESSOR: { bg: 'linear-gradient(135deg, #6366f1, #8b5cf6)', accent: '#6366f1' },
  ADMIN: { bg: 'linear-gradient(135deg, #1e293b, #334155)', accent: '#64748b' },
};

function UserProfileDialog({ user, turmaList, onClose, onImpersonate, impersonating }: {
  user: any; turmaList: any[]; onClose: () => void;
  onImpersonate: (uid: string) => void; impersonating: string | null;
}) {
  const cfg = CARGO_CONFIG[user.cargo] || CARGO_CONFIG[user.perfil === 'PROFESSOR' ? 'PROFESSOR' : 'DIRETOR'];
  const colors = PROFILE_COLORS[user.perfil] || PROFILE_COLORS.ESCOLA;
  const nome = user.nome?.replace(/^(Prof\.\s*|Dir\.\s*)/i, '') || user.nome;

  // Para professores: encontrar turmas e matérias vinculadas
  const profTurmas = user.perfil === 'PROFESSOR'
    ? turmaList.filter((t: any) => (t.professores || []).some((pt: any) => pt.professorId === user.id || pt.professor?.id === user.id))
    : [];

  const DIA_ABREV: Record<string, string> = {
    SEGUNDA: 'Seg', TERCA: 'Ter', QUARTA: 'Qua', QUINTA: 'Qui', SEXTA: 'Sex', SABADO: 'Sáb',
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'fadeIn .2s ease',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 'min(95vw, 480px)', maxHeight: '90vh', background: 'var(--bg-card, white)',
        borderRadius: 20, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        border: '1px solid var(--border-color, #e5e7eb)',
        boxShadow: '0 25px 60px rgba(0,0,0,.3)',
      }}>
        {/* Header com foto */}
        <div style={{
          background: colors.bg, padding: '28px 24px 20px',
          display: 'flex', alignItems: 'center', gap: 18, position: 'relative',
        }}>
          <button onClick={onClose} style={{
            position: 'absolute', top: 12, right: 12, width: 28, height: 28, borderRadius: 8,
            background: 'rgba(255,255,255,.15)', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
          }}><X size={14} /></button>
          <div style={{
            width: 88, height: 88, borderRadius: 18, flexShrink: 0, overflow: 'hidden',
            background: user.fotoUrl ? `url(${user.fotoUrl}) center/cover` : 'rgba(255,255,255,.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontWeight: 800, fontSize: '2rem',
            border: '3px solid rgba(255,255,255,.3)', boxShadow: '0 4px 16px rgba(0,0,0,.2)',
          }}>
            {!user.fotoUrl && (nome.charAt(0)?.toUpperCase() || '?')}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'white', lineHeight: 1.2 }}>{nome}</div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 6,
              padding: '3px 10px', borderRadius: 6, fontSize: '.68rem', fontWeight: 700,
              background: 'rgba(255,255,255,.2)', color: 'white',
            }}>
              {cfg.label}
            </div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6,
              padding: '3px 8px', borderRadius: 6, fontSize: '.62rem', fontWeight: 600,
              background: user.ativo ? 'rgba(16,185,129,.3)' : 'rgba(239,68,68,.3)',
              color: 'white',
            }}>
              {user.ativo ? '● Ativo' : '● Inativo'}
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 24px 24px' }}>
          {/* Contact info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
            {user.email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: `${colors.accent}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Mail size={14} style={{ color: colors.accent }} />
                </div>
                <a href={`mailto:${user.email}`} style={{ fontSize: '.82rem', color: '#3b82f6', textDecoration: 'none' }} title={`Enviar e-mail para ${user.email}`}>{user.email}</a>
              </div>
            )}
            {user.telefone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: '#25d36612', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Phone size={14} style={{ color: '#25d366' }} />
                </div>
                <a href={`https://wa.me/55${user.telefone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: '.82rem', color: '#25d366', textDecoration: 'none', fontWeight: 500 }} title="Abrir WhatsApp">
                  {user.telefone}
                </a>
              </div>
            )}
          </div>

          {/* Info grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 18 }}>
            {[
              { label: 'Formação', value: user.formacao },
              { label: 'Registro Prof.', value: user.registroProf },
              { label: 'Especialidade', value: user.especialidade },
              { label: 'Desde', value: user.criadoEm ? new Date(user.criadoEm).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }) : null },
              { label: 'Último acesso', value: user.ultimoLogin ? new Date(user.ultimoLogin).toLocaleDateString('pt-BR') : null },
              { label: 'Status', value: user.statusConta },
            ].filter(x => x.value).map((item, i) => (
              <div key={i} style={{
                padding: '10px 12px', borderRadius: 10,
                background: 'var(--bg-main, #f8fafc)', border: '1px solid var(--border-color, #e5e7eb)',
              }}>
                <div style={{ fontSize: '.62rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '.5px' }}>{item.label}</div>
                <div style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--text-main)' }}>{item.value}</div>
              </div>
            ))}
          </div>

          {/* Turmas: Série → Matéria → Turno → Turmas (professor) */}
          {user.perfil === 'PROFESSOR' && profTurmas.length > 0 && (() => {
            const TURNO_LABELS: Record<string, string> = { MANHA: 'Manhã', TARDE: 'Tarde', NOITE: 'Noite', INTEGRAL: 'Integral' };
            const TURNO_COLORS: Record<string, string> = {
              MANHA: '#0369a1',
              TARDE: '#b45309',
              NOITE: '#5b21b6',
              INTEGRAL: '#047857',
            };
            // Parse turma data
            const parsed = profTurmas.map((t: any) => {
              const vinculoProf = (t.professores || []).find((pt: any) => pt.professorId === user.id || pt.professor?.id === user.id);
              const matNome = vinculoProf?.materia?.nome || '—';
              const dias = (t.horarios || [])
                .filter((h: any) => (h.professorId === user.id) && h.tipo === 'AULA')
                .map((h: any) => DIA_ABREV[h.diaSemana] || h.diaSemana);
              const diasUnicos = [...new Set(dias)];
              const parts = (t.nome || '').split(/\s*-\s*/);
              const serie = parts[0]?.trim() || t.nome;
              const turmaLetra = parts[1]?.replace(/TURMA\s*/i, '').trim() || '';
              const alunoCount = t._count?.alunos ?? t.alunos?.length ?? null;
              const turno = t.turno || t.turnoOperacional?.nome || '';
              return { ...t, serie, turmaLetra, matNome, diasUnicos, alunoCount, turnoKey: turno };
            });

            // Build hierarchy: Serie → Materia → Turno → turmas[]
            type TurmaItem = typeof parsed[0];
            const bySerie = new Map<string, Map<string, Map<string, TurmaItem[]>>>();
            for (const t of parsed) {
              if (!bySerie.has(t.serie)) bySerie.set(t.serie, new Map());
              const materias = bySerie.get(t.serie)!;
              if (!materias.has(t.matNome)) materias.set(t.matNome, new Map());
              const turnos = materias.get(t.matNome)!;
              const tk = t.turnoKey || '_';
              if (!turnos.has(tk)) turnos.set(tk, []);
              turnos.get(tk)!.push(t);
            }

            return (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <BookOpen size={16} style={{ color: '#475569' }} />
                  <span style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--text-main)' }}>
                    Turmas & Horários
                  </span>
                  <span style={{ fontSize: '.78rem', color: 'var(--text-muted)', fontWeight: 600, marginLeft: 'auto' }}>
                    {profTurmas.length} turma{profTurmas.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[...bySerie.entries()].map(([serie, materias]) => {
                    let serieTotal = 0, serieAlunos = 0;
                    materias.forEach(turnos => turnos.forEach(ts => { serieTotal += ts.length; serieAlunos += ts.reduce((s, t) => s + (t.alunoCount || 0), 0); }));
                    return (
                      <div key={serie} style={{
                        padding: '12px 14px', borderRadius: 12,
                        background: 'var(--bg-main, #f8fafc)', border: '1px solid var(--border-color, #e5e7eb)',
                        textTransform: 'uppercase',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: '.92rem', color: 'var(--text-main)' }}>{serie}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ fontSize: '.7rem', color: '#475569', fontWeight: 700, background: '#47556912', padding: '2px 7px', borderRadius: 5 }}>
                              {serieTotal} turma{serieTotal > 1 ? 's' : ''}
                            </span>
                            {serieAlunos > 0 && (
                              <span style={{ fontSize: '.7rem', color: '#065f46', fontWeight: 700, background: '#065f4612', padding: '2px 7px', borderRadius: 5 }}>
                                {serieAlunos} aluno{serieAlunos > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        {[...materias.entries()].map(([matNome, turnos]) => (
                          <div key={matNome} style={{ marginBottom: materias.size > 1 ? 6 : 0, paddingLeft: 10 }}>
                            <div style={{ fontSize: '.82rem', fontWeight: 700, color: '#334155', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 5 }}>
                              <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#64748b' }} />
                              {matNome}
                            </div>
                            <div style={{ paddingLeft: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {[...turnos.entries()].map(([turnoKey, turnoTurmas]) => {
                                const turnoColor = TURNO_COLORS[turnoKey] || '#475569';
                                const turnoLabel = TURNO_LABELS[turnoKey] || '';
                                return (
                                  <div key={turnoKey} style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                    {turnoLabel && turnos.size > 1 && (
                                      <span style={{
                                        fontSize: '.62rem', fontWeight: 700, color: turnoColor,
                                        border: `1.5px solid ${turnoColor}`, padding: '2px 7px', borderRadius: 5,
                                        textTransform: 'uppercase', letterSpacing: '.3px', minWidth: 48, textAlign: 'center',
                                      }}>
                                        {turnoLabel}
                                      </span>
                                    )}
                                    {turnoTurmas.map(t => (
                                      <span key={t.id} style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                        padding: '4px 9px', borderRadius: 6,
                                        background: '#f1f5f9', border: '1px solid #e2e8f0',
                                        fontSize: '.78rem', fontWeight: 700, color: '#1e293b',
                                      }}>
                                        Turma {t.turmaLetra || t.nome}
                                        {t.alunoCount != null && (
                                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: '#64748b', fontWeight: 600, fontSize: '.68rem' }}>
                                            <Users size={10} />{t.alunoCount}
                                          </span>
                                        )}
                                      </span>
                                    ))}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Fixed footer — Botão Entrar */}
        {user.ativo && (
          <div style={{
            padding: '12px 24px 16px', borderTop: '1px solid var(--border-color, #e5e7eb)',
            background: 'var(--bg-card, white)', flexShrink: 0,
          }}>
            <button onClick={() => onImpersonate(user.id)} disabled={impersonating === user.id} style={{
              width: '100%', padding: '12px 16px', borderRadius: 12, fontSize: '.88rem', fontWeight: 700,
              background: colors.bg, color: 'white', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              opacity: impersonating === user.id ? .6 : 1,
              boxShadow: '0 4px 14px rgba(0,0,0,.15)',
            }}>
              <LogIn size={16} />
              {impersonating === user.id ? 'Abrindo...' : 'Entrar como este usuário'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AlunoProfileDialog ───────────────────────────────────────────────────────

function useAlunoDetalhe(escolaId: string, alunoId: string | null) {
  return useQuery({
    queryKey: ['aluno-detalhe', escolaId, alunoId],
    queryFn: async () => {
      const res = await fetch(`${API}/escolas/${escolaId}/alunos/${alunoId}`, { headers: headers() });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!escolaId && !!alunoId,
  });
}

function AlunoProfileDialog({ aluno: alunoBasico, escolaId, onClose, onImpersonate, impersonating }: {
  aluno: any; escolaId: string; onClose: () => void;
  onImpersonate: (uid: string) => void; impersonating: string | null;
}) {
  const { data: alunoCompleto, isLoading } = useAlunoDetalhe(escolaId, alunoBasico?.id);
  const aluno = alunoCompleto || alunoBasico;

  const idade = aluno.dataNascimento
    ? Math.floor((Date.now() - new Date(aluno.dataNascimento).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    : null;

  const responsaveis = aluno.responsaveis || [];
  const tags = aluno.tags || [];

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'fadeIn .2s ease',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 'min(95vw, 480px)', maxHeight: '90vh', background: 'var(--bg-card, white)',
        borderRadius: 20, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        border: '1px solid var(--border-color, #e5e7eb)',
        boxShadow: '0 25px 60px rgba(0,0,0,.3)',
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #f97316, #fb923c)', padding: '28px 24px 20px',
          display: 'flex', alignItems: 'center', gap: 18, position: 'relative',
        }}>
          <button onClick={onClose} style={{
            position: 'absolute', top: 12, right: 12, width: 28, height: 28, borderRadius: 8,
            background: 'rgba(255,255,255,.15)', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
          }}><X size={14} /></button>
          <div style={{
            width: 88, height: 88, borderRadius: 18, flexShrink: 0, overflow: 'hidden',
            background: aluno.fotoUrl ? `url(${aluno.fotoUrl}) center/cover` : 'rgba(255,255,255,.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontWeight: 800, fontSize: '2rem',
            border: '3px solid rgba(255,255,255,.3)', boxShadow: '0 4px 16px rgba(0,0,0,.2)',
          }}>
            {!aluno.fotoUrl && (aluno.nome?.charAt(0)?.toUpperCase() || '?')}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'white', lineHeight: 1.2 }}>{aluno.nome}</div>
            {aluno.matricula && (
              <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.8)', marginTop: 4 }}>Mat: {aluno.matricula}</div>
            )}
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              {aluno.turma && (
                <span style={{
                  padding: '3px 10px', borderRadius: 6, fontSize: '.66rem', fontWeight: 700,
                  background: 'rgba(255,255,255,.2)', color: 'white',
                }}>{aluno.turma.nome}</span>
              )}
              {idade && (
                <span style={{
                  padding: '3px 8px', borderRadius: 6, fontSize: '.66rem', fontWeight: 600,
                  background: 'rgba(255,255,255,.15)', color: 'white',
                }}>{idade} anos</span>
              )}
              {aluno.genero && (
                <span style={{
                  padding: '3px 8px', borderRadius: 6, fontSize: '.66rem', fontWeight: 600,
                  background: 'rgba(255,255,255,.15)', color: 'white',
                }}>{aluno.genero === 'MASCULINO' ? 'Masc.' : 'Fem.'}</span>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 24px 24px' }}>
          {isLoading && (
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: '.8rem' }}>Carregando dados completos...</div>
          )}

          {/* Info grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 18 }}>
            {[
              { label: 'Ingresso', value: aluno.anoIngresso },
              { label: 'Nascimento', value: aluno.dataNascimento ? new Date(aluno.dataNascimento).toLocaleDateString('pt-BR') : null },
              { label: 'Naturalidade', value: aluno.naturalidade },
            ].filter(x => x.value).map((item, i) => (
              <div key={i} style={{
                padding: '10px 12px', borderRadius: 10,
                background: 'var(--bg-main, #f8fafc)', border: '1px solid var(--border-color, #e5e7eb)',
              }}>
                <div style={{ fontSize: '.62rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '.5px' }}>{item.label}</div>
                <div style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--text-main)' }}>{item.value}</div>
              </div>
            ))}
          </div>

          {/* Responsáveis */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <Users size={14} style={{ color: '#8b5cf6' }} />
              <span style={{ fontWeight: 700, fontSize: '.78rem', color: 'var(--text-main)' }}>Responsáveis</span>
              <span style={{ padding: '1px 6px', borderRadius: 8, fontSize: '.6rem', fontWeight: 600, background: '#8b5cf615', color: '#8b5cf6' }}>{responsaveis.length}</span>
            </div>
            {responsaveis.length === 0 ? (
              <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', fontStyle: 'italic', padding: '8px 0' }}>Nenhum responsável vinculado</div>
            ) : responsaveis.map((ra: any, i: number) => {
              const r = ra.responsavel || ra;
              const temConta = !!(r.usuarioId || r.usuario);
              return (
                <div key={r.id || i} style={{
                  padding: '12px 14px', borderRadius: 10, marginBottom: 6,
                  background: 'var(--bg-main, #f8fafc)', border: '1px solid var(--border-color, #e5e7eb)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: '.82rem', color: 'var(--text-main)' }}>{r.nome}</span>
                      {ra.grauParentesco && (
                        <span style={{ padding: '1px 7px', borderRadius: 5, fontSize: '.6rem', fontWeight: 600, background: '#8b5cf610', color: '#8b5cf6' }}>{ra.grauParentesco}</span>
                      )}
                      {ra.principal && (
                        <span style={{ padding: '1px 6px', borderRadius: 5, fontSize: '.58rem', fontWeight: 700, background: '#f59e0b20', color: '#f59e0b' }}>Principal</span>
                      )}
                    </div>
                    {temConta && r.usuarioId && (
                      <button onClick={() => onImpersonate(r.usuarioId)} disabled={impersonating === r.usuarioId} style={{
                        padding: '3px 10px', fontSize: '.64rem', fontWeight: 600, borderRadius: 6,
                        background: '#6366f115', color: '#6366f1', border: '1px solid #6366f130', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        <LogIn size={10} /> Entrar
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {r.telefone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.72rem', color: 'var(--text-muted)' }}>
                        <Phone size={11} /> {r.telefone}
                      </div>
                    )}
                    {r.email && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.72rem', color: 'var(--text-muted)' }}>
                        <Mail size={11} /> {r.email}
                      </div>
                    )}
                    {!temConta && (
                      <span style={{ fontSize: '.64rem', padding: '1px 6px', borderRadius: 4, background: '#9ca3af15', color: '#9ca3af', fontWeight: 600 }}>Sem conta</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tags RFID */}
          {tags.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <Tag size={14} style={{ color: '#0ea5e9' }} />
                <span style={{ fontWeight: 700, fontSize: '.78rem', color: 'var(--text-main)' }}>Tags RFID</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {tags.map((tag: any) => (
                  <div key={tag.id} style={{
                    padding: '6px 12px', borderRadius: 8,
                    background: 'var(--bg-main, #f8fafc)', border: '1px solid var(--border-color, #e5e7eb)',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: tag.ativa ? '#10b981' : '#ef4444',
                    }} />
                    <span style={{ fontSize: '.74rem', fontWeight: 600, color: 'var(--text-main)' }}>{tag.codigo}</span>
                    <span style={{ fontSize: '.62rem', color: 'var(--text-muted)' }}>{tag.ativa ? 'Ativa' : 'Inativa'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CARGO_CONFIG ─────────────────────────────────────────────────────────────

const CARGO_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  DIRETOR: { label: 'DIRETOR(A)', color: '#10b981', icon: Shield },
  COORDENACAO: { label: 'COORDENAÇÃO', color: '#14b8a6', icon: Shield },
  SECRETARIA: { label: 'SECRETARIA', color: '#0ea5e9', icon: Shield },
  PROFESSOR: { label: 'PROFESSOR(A)', color: '#f59e0b', icon: GraduationCap },
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export function EscolaDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: escola, isLoading } = useEscolaDetalhe(id || '');
  const { data: equipe } = useEscolaEquipe(id || '');
  const { data: turmas } = useEscolaTurmas(id || '');
  const { data: materias } = useEscolaMaterias(id || '');
  const { impersonate: handleImpersonate, impersonating } = useImpersonate();
  const [selectedTurma, setSelectedTurma] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedAluno, setSelectedAluno] = useState<any>(null);

  // Organize equipe by cargo
  const diretores = (equipe || []).filter((u: any) => u.perfil === 'ESCOLA' && (u.cargo === 'DIRETOR' || !u.cargo));
  const coordenadores = (equipe || []).filter((u: any) => u.perfil === 'ESCOLA' && u.cargo === 'COORDENACAO');
  const secretarias = (equipe || []).filter((u: any) => u.perfil === 'ESCOLA' && u.cargo === 'SECRETARIA');
  const professores = (equipe || []).filter((u: any) => u.perfil === 'PROFESSOR');

  const totalAlunos = escola?._count?.alunos || escola?.totalAlunos || 0;
  const totalTurmas = escola?._count?.turmas || 0;
  const totalTurnos = escola?.turnosOperacionais?.length || 0;
  const totalMaterias = (materias || []).length;
  const totalEquipe = diretores.length + coordenadores.length + secretarias.length;

  if (isLoading) return <div className="admin-page animate-fadeIn" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>;
  if (!escola) return <div className="admin-page animate-fadeIn" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Escola nao encontrada</div>;

  // ─── Turno grouping ───
  const DIA_ABREV: Record<string, string> = {
    SEGUNDA: 'Seg', TERCA: 'Ter', QUARTA: 'Qua', QUINTA: 'Qui', SEXTA: 'Sex', SABADO: 'Sab',
  };

  // ─── Turno grouping (sorted by horaAberturaPortoes) ───
  const turmaList: any[] = Array.isArray(turmas) ? turmas : [];
  const turnoMap = new Map<string, { turno: any; turmas: any[] }>();
  const semTurno: any[] = [];
  for (const t of turmaList) {
    if (t.turnoOperacionalId && t.turnoOperacional) {
      const key = t.turnoOperacionalId;
      if (!turnoMap.has(key)) turnoMap.set(key, { turno: t.turnoOperacional, turmas: [] });
      turnoMap.get(key)!.turmas.push(t);
    } else {
      semTurno.push(t);
    }
  }
  // Sort turnos by horaAberturaPortoes (e.g. "06:40" < "13:00" < "18:00")
  const turnoEntries = [...turnoMap.entries()].sort(([, a], [, b]) =>
    (a.turno.horaAberturaPortoes || '').localeCompare(b.turno.horaAberturaPortoes || '')
  );

  // Build turno color map for badges
  const TURNO_COLORS: Record<string, string> = {};
  const TURNO_PALETTE = ['#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#6b7280'];
  turnoEntries.forEach(([key], i) => { TURNO_COLORS[key] = TURNO_PALETTE[i % TURNO_PALETTE.length]; });

  const groupBySerie = (list: any[]) => {
    const map = new Map<string, any[]>();
    for (const t of list) {
      const s = t.serie || 'Sem Serie';
      if (!map.has(s)) map.set(s, []);
      map.get(s)!.push(t);
    }
    // Sort turmas within each serie by turno order (earliest first)
    for (const [, arr] of map) {
      arr.sort((a: any, b: any) => {
        const ha = a.turnoOperacional?.horaAberturaPortoes || 'zz';
        const hb = b.turnoOperacional?.horaAberturaPortoes || 'zz';
        return ha.localeCompare(hb) || (a.nome || '').localeCompare(b.nome || '');
      });
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  };

  // Build professor info from horarios + professorTurma
  const buildProfInfo = (turma: any) => {
    const profsMap = new Map<string, { id: string; nome: string; fotoUrl: string | null; materias: Map<string, Set<string>> }>();
    // From ProfessorTurma
    for (const pt of (turma.professores || [])) {
      const p = pt.professor;
      if (!p) continue;
      if (!profsMap.has(p.id)) profsMap.set(p.id, { id: p.id, nome: p.nome, fotoUrl: p.fotoUrl, materias: new Map() });
      const entry = profsMap.get(p.id)!;
      const matNome = pt.materia?.nome || '?';
      if (!entry.materias.has(matNome)) entry.materias.set(matNome, new Set());
    }
    // Enrich with horario days
    for (const h of (turma.horarios || [])) {
      if (!h.professorId || !h.materia) continue;
      const entry = profsMap.get(h.professorId);
      if (!entry) continue;
      const matNome = h.materia.nome;
      if (!entry.materias.has(matNome)) entry.materias.set(matNome, new Set());
      entry.materias.get(matNome)!.add(h.diaSemana);
    }
    return [...profsMap.values()];
  };

  const ALL_DIAS = ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO', 'DOMINGO'] as const;
  const DIA_LETTER: Record<string, string> = { SEGUNDA: 'S', TERCA: 'T', QUARTA: 'Q', QUINTA: 'Q', SEXTA: 'S', SABADO: 'S', DOMINGO: 'D' };
  const DIA_LABEL_FULL: Record<string, string> = { SEGUNDA: 'Seg', TERCA: 'Ter', QUARTA: 'Qua', QUINTA: 'Qui', SEXTA: 'Sex', SABADO: 'Sáb', DOMINGO: 'Dom' };

  const renderTurmaCard = (t: any, turnoColor: string) => {
    const profInfo = buildProfInfo(t);
    const tintBg = `${turnoColor}08`;
    return (
      <div key={t.id} style={{
        padding: '10px 12px', borderRadius: 8, background: tintBg,
        border: `1px solid ${turnoColor}18`, transition: 'all .2s',
      }}>
        {/* Turma name + alunos */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontWeight: 700, fontSize: '.8rem', color: 'var(--text-main)', textTransform: 'uppercase' }}>
            {(t.serie && t.nome?.startsWith(t.serie)) ? t.nome.slice(t.serie.length).replace(/^\s*-\s*/, '') : t.nome}
          </span>
          <span style={{ padding: '2px 6px', borderRadius: 5, fontSize: '.58rem', fontWeight: 700, background: '#f59e0b15', color: '#f59e0b', flexShrink: 0 }}>
            {t._count?.alunos || 0}
          </span>
        </div>
        {t.sala && <div style={{ fontSize: '.6rem', color: 'var(--text-muted)', marginBottom: 4 }}>Sala {t.sala}</div>}

        {/* Professores */}
        {profInfo.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
            {profInfo.map((p) => (
              <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                {/* Avatar — click opens profile */}
                <div style={{
                  width: 30, height: 30, borderRadius: 8, flexShrink: 0, overflow: 'hidden', cursor: 'pointer',
                  background: p.fotoUrl ? `url(${p.fotoUrl}) center/cover` : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: 700, fontSize: '.65rem',
                }}
                  onClick={() => { const usr = professores.find((u: any) => u.id === p.id); if (usr) setSelectedUser(usr); }}
                >
                  {!p.fotoUrl && (p.nome?.charAt(0)?.toUpperCase() || '?')}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Name — click opens profile */}
                  <div style={{ fontWeight: 600, fontSize: '.72rem', color: 'var(--text-main)', lineHeight: 1.2, cursor: 'pointer' }}
                    onClick={() => { const usr = professores.find((u: any) => u.id === p.id); if (usr) setSelectedUser(usr); }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = ''; }}
                  >
                    {p.nome.replace(/^Prof\.\s*/i, '')}
                  </div>
                  {/* Matéria UPPERCASE + Day badges */}
                  {[...p.materias.entries()].map(([mat, dias]) => (
                    <div key={mat} style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
                      <span style={{
                        fontSize: '.54rem', padding: '1px 5px', borderRadius: 4,
                        background: '#3b82f612', color: '#3b82f6', fontWeight: 700, textTransform: 'uppercase', flexShrink: 0,
                      }}>
                        {mat}
                      </span>
                      <div style={{ display: 'flex', gap: 1, marginLeft: 'auto', flexShrink: 0 }}>
                        {ALL_DIAS.map(dia => {
                          const active = dias.has(dia);
                          return (
                            <span key={dia} title={`${DIA_LABEL_FULL[dia]}${active ? ' — clique para detalhes' : ''}`} style={{
                              width: 15, height: 15, borderRadius: 3, fontSize: '.44rem', fontWeight: 700,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              background: active ? '#10b981' : '#e2e8f0',
                              color: active ? 'white' : '#94a3b8',
                              cursor: active ? 'pointer' : 'default',
                              transition: 'transform .1s',
                            }}
                              onClick={(e) => {
                                if (!active) return;
                                e.stopPropagation();
                                const horario = (t.horarios || []).find((h: any) =>
                                  h.professorId === p.id && h.diaSemana === dia && h.materia?.nome === mat
                                );
                                const horaIni = horario?.horaInicio || '-';
                                const horaFim = horario?.horaFim || '-';
                                const turmaLabel = (t.serie && t.nome?.startsWith(t.serie)) ? t.nome.slice(t.serie.length).replace(/^\s*-\s*/, '') : t.nome;
                                toast.info(`📅 ${DIA_LABEL_FULL[dia]} — ${mat}`, {
                                  description: `Professor: ${p.nome} | Turma: ${t.serie} — ${turmaLabel} | Horário: ${horaIni} às ${horaFim} (${t._count?.alunos || 0} alunos)`
                                });
                              }}
                              onMouseEnter={(e) => { if (active) (e.currentTarget as HTMLElement).style.transform = 'scale(1.2)'; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = ''; }}
                            >
                              {DIA_LETTER[dia]}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '.66rem', color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 8 }}>Sem professor</div>
        )}

        {/* Ver Alunos */}
        <button onClick={() => setSelectedTurma(t)} style={{
          width: '100%', padding: '5px 0', borderRadius: 7, fontSize: '.7rem', fontWeight: 600,
          background: 'rgba(255,255,255,.5)', color: '#10b981', border: '1px solid #10b98120',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        }}>
          <Users size={11} /> Ver Alunos
        </button>
      </div>
    );
  };

  const renderKanbanColumns = (groups: [string, any[]][]) => (
    <div style={{
      display: 'flex', gap: 12, overflowX: 'auto', overflowY: 'hidden',
      paddingBottom: 6, scrollSnapType: 'x mandatory',
      height: '100%',
    }}>
      {groups.map(([serie, list]) => {
        // Group turmas by turno within this série
        const byTurno = new Map<string, { turno: any; turmas: any[] }>();
        for (const t of list) {
          const tId = t.turnoOperacionalId || '_sem_';
          if (!byTurno.has(tId)) byTurno.set(tId, { turno: t.turnoOperacional, turmas: [] });
          byTurno.get(tId)!.turmas.push(t);
        }
        const turnoGroups = [...byTurno.entries()].sort(([, a], [, b]) =>
          (a.turno?.horaAberturaPortoes || '').localeCompare(b.turno?.horaAberturaPortoes || '')
        );

        return (
          <div key={serie} style={{
            minWidth: 260, maxWidth: 300, flexShrink: 0, scrollSnapAlign: 'start',
            background: 'var(--bg-main, #f8fafc)', borderRadius: 12,
            border: '1px solid var(--border-color, #e5e7eb)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            {/* Sticky serie header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 10px 6px', borderBottom: '2px solid #8b5cf620',
              background: 'var(--bg-main, #f8fafc)',
              position: 'sticky', top: 0, zIndex: 2,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <BookOpen size={13} style={{ color: '#8b5cf6' }} />
                <span style={{ fontWeight: 700, fontSize: '.74rem', color: 'var(--text-main)', textTransform: 'uppercase' }}>{serie}</span>
              </div>
              <span style={{ padding: '1px 6px', borderRadius: 6, fontSize: '.58rem', fontWeight: 700, background: '#8b5cf615', color: '#8b5cf6' }}>{list.length}</span>
            </div>
            {/* Scrollable: turno groups */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px 10px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {turnoGroups.map(([tId, { turno, turmas }]) => {
                const tc = TURNO_COLORS[tId] || '#6b7280';
                const horaIni = turno?.horaAberturaPortoes || '';
                const horaFim = turno?.horaSaidaOficial || '';
                const turnoNome = turno?.nome || 'Sem Turno';
                return (
                  <div key={tId} style={{ borderRadius: 10, overflow: 'hidden', border: `1px solid ${tc}25` }}>
                    {/* Turno header bar */}
                    <div style={{
                      background: tc, padding: '6px 10px',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      <span style={{ fontSize: '.58rem', fontWeight: 600, color: 'rgba(255,255,255,.8)' }}>{horaIni}</span>
                      <span style={{ fontSize: '.66rem', fontWeight: 800, color: 'white', textTransform: 'uppercase', letterSpacing: '.5px' }}>{turnoNome}</span>
                      <span style={{ fontSize: '.58rem', fontWeight: 600, color: 'rgba(255,255,255,.8)' }}>{horaFim}</span>
                    </div>
                    {/* Turma cards */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 6, background: `${tc}05` }}>
                      {turmas.map(t => renderTurmaCard(t, tc))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="admin-page animate-fadeIn">
      {/* ═══ HERO BANNER ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #065f46 0%, #047857 40%, #059669 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(16,185,129,.12)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <button onClick={() => navigate('/master/escolas')} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8,
            background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.7)',
            fontSize: '.78rem', fontWeight: 600, cursor: 'pointer', marginBottom: 14,
          }}>
            <ArrowLeft size={14} /> Voltar para Escolas
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Building2 size={18} style={{ color: '#6ee7b7' }} />
            <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.12em' }}>Escola</span>
          </div>
          <h1 style={{ color: 'white', fontSize: '1.4rem', fontWeight: 800, margin: 0 }}>{escola.nome}</h1>
          <p style={{ color: 'rgba(255,255,255,.45)', fontSize: '.82rem', marginTop: 4 }}>
            {escola.cidade || ''}{escola.uf ? ` / ${escola.uf}` : ''} {escola.tipoRede ? ` \u2022 ${escola.tipoRede}` : ''}
            {escola.codigoINEP ? ` \u2022 INEP: ${escola.codigoINEP}` : ''}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 20, marginTop: 22 }}>
            {[
              { icon: Users, value: totalAlunos.toLocaleString('pt-BR'), label: 'Alunos', color: '#6ee7b7' },
              { icon: GraduationCap, value: professores.length, label: 'Professores', color: '#fbbf24' },
              { icon: Shield, value: totalEquipe, label: 'Equipe', color: '#93c5fd' },
              { icon: BookOpen, value: totalTurmas, label: 'Turmas', color: '#c4b5fd' },
              { icon: Clock, value: totalTurnos, label: 'Turnos', color: '#f9a8d4' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${s.color}20`, color: s.color }}>
                  <s.icon size={20} />
                </div>
                <div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.55)', fontWeight: 500 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ BLOCO 1: GESTÃO ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 16, marginBottom: 20 }}>
        {/* LEFT: Diretor + Equipe */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Diretor */}
          <div style={{
            background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)',
            padding: 18, borderLeft: '4px solid #10b981',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <Shield size={16} style={{ color: '#10b981' }} />
              <span style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--text-main)' }}>Direção</span>
              <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: '.65rem', fontWeight: 600, background: '#10b98115', color: '#10b981' }}>{diretores.length}</span>
            </div>
            {diretores.length === 0 ? (
              <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Nenhum diretor vinculado</div>
            ) : diretores.map((u: any) => (
              <div key={u.id} onClick={() => setSelectedUser(u)} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0', borderBottom: '1px solid var(--border-color, #e5e7eb)', cursor: 'pointer', transition: 'background .15s', borderRadius: 8 }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-main, #f8fafc)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
              >
                <div style={{
                  width: 72, height: 72, borderRadius: 16, flexShrink: 0, overflow: 'hidden',
                  background: u.fotoUrl ? `url(${u.fotoUrl}) center/cover` : 'linear-gradient(135deg, #10b981, #059669)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: 700, fontSize: '1.4rem', boxShadow: '0 3px 12px rgba(16,185,129,.25)',
                }}>
                  {!u.fotoUrl && (u.nome?.charAt(0)?.toUpperCase() || '?')}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '.88rem', color: 'var(--text-main)' }}>{u.nome}</div>
                  <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', marginTop: 2 }}>{u.email}</div>
                  <div style={{ fontSize: '.62rem', color: '#10b981', marginTop: 4, fontWeight: 500 }}>Clique para ver perfil</div>
                </div>
              </div>
            ))}
          </div>

          {/* Equipe (abaixo do Diretor) */}
          <div style={{
            background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)',
            padding: 18, borderLeft: '4px solid #8b5cf6',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <Users size={16} style={{ color: '#8b5cf6' }} />
              <span style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--text-main)' }}>Equipe</span>
            </div>
            {coordenadores.length + secretarias.length === 0 ? (
              <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Nenhum coordenador ou secretário</div>
            ) : [...coordenadores, ...secretarias].map((u: any) => {
              const cfg = CARGO_CONFIG[u.cargo] || CARGO_CONFIG.COORDENACAO;
              return (
                <div key={u.id} onClick={() => setSelectedUser(u)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                  borderBottom: '1px solid var(--border-color, #e5e7eb)',
                  cursor: 'pointer', transition: 'background .15s', borderRadius: 8,
                }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-main, #f8fafc)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
                >
                  <div style={{
                    width: 56, height: 56, borderRadius: 14, flexShrink: 0, overflow: 'hidden',
                    background: u.fotoUrl ? `url(${u.fotoUrl}) center/cover` : `linear-gradient(135deg, ${cfg.color}, ${cfg.color}cc)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontWeight: 700, fontSize: '1.1rem', boxShadow: `0 3px 12px ${cfg.color}30`,
                  }}>
                    {!u.fotoUrl && (u.nome?.charAt(0)?.toUpperCase() || '?')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '.82rem', color: 'var(--text-main)' }}>{u.nome}</div>
                    <div style={{ fontSize: '.64rem', color: cfg.color, fontWeight: 600, marginTop: 1 }}>{cfg.label}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detalhes da Escola (below Equipe) */}
          <div style={{
            background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)',
            padding: 18, borderLeft: '4px solid #3b82f6',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <MapPin size={16} style={{ color: '#3b82f6' }} />
              <span style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--text-main)' }}>Detalhes da Escola</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              {[
                { label: 'Endereco', value: escola.endereco || '-' },
                { label: 'Cidade / UF', value: `${escola.cidade || '-'} / ${escola.uf || '-'}` },
                { label: 'CEP', value: escola.cep || '-' },
                { label: 'Telefone', value: escola.telefone || '-' },
                { label: 'Email', value: escola.email || '-' },
                { label: 'Codigo', value: escola.codigo || '-' },
                { label: 'Tipo Rede', value: escola.tipoRede || '-' },
                { label: 'INEP', value: escola.codigoINEP || '-' },
              ].filter(item => item.value !== '-').map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid var(--border-color, #f1f5f9)' }}>
                  <span style={{ fontSize: '.64rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{item.label}</span>
                  <span style={{ fontSize: '.76rem', fontWeight: 600, color: 'var(--text-main)', textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.value}</span>
                </div>
              ))}
            </div>
            {totalMaterias > 0 && (
              <div>
                <div style={{ fontSize: '.64rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 5 }}>Matérias ({totalMaterias})</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {(materias || []).map((m: any) => (
                    <span key={m.id} style={{
                      padding: '2px 8px', borderRadius: 5, fontSize: '.62rem', fontWeight: 600,
                      background: '#8b5cf615', color: '#8b5cf6', border: '1px solid #8b5cf620',
                    }}>{m.nome}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Professores — Photo Cards */}
        <div style={{
          background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)',
          padding: 18,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <GraduationCap size={16} style={{ color: '#f59e0b' }} />
            <span style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--text-main)' }}>Professores</span>
            <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: '.65rem', fontWeight: 600, background: '#f59e0b15', color: '#f59e0b' }}>{professores.length}</span>
          </div>
          {professores.length === 0 ? (
            <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Nenhum professor vinculado</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 10 }}>
              {professores.map((u: any) => {
                const nome = u.nome.replace(/^Prof\.\s*/i, '');
                return (
                  <div key={u.id} style={{
                    borderRadius: 12, overflow: 'hidden', cursor: 'pointer', position: 'relative',
                    border: '1px solid var(--border-color, #e5e7eb)',
                    background: 'var(--bg-main, #f8fafc)', transition: 'all .2s',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 20px rgba(0,0,0,.12)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = ''; (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
                  onClick={() => setSelectedUser(u)}
                  >
                    {/* Photo */}
                    <div style={{
                      width: '100%', aspectRatio: '1', overflow: 'hidden',
                      background: u.fotoUrl ? `url(${u.fotoUrl}) center/cover` : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 800, fontSize: '1.6rem',
                    }}>
                      {!u.fotoUrl && (nome.charAt(0)?.toUpperCase() || '?')}
                    </div>
                    {/* Name footer */}
                    <div style={{
                      padding: '8px 6px', textAlign: 'center',
                      background: 'var(--bg-card, white)',
                    }}>
                      <div style={{
                        fontWeight: 700, fontSize: '.72rem', color: 'var(--text-main)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        lineHeight: 1.2,
                      }}>{nome}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>



      {/* ═══ BLOCO 3: TURMAS — KANBAN UNIFICADO ═══ */}
      <div style={{
        background: 'var(--bg-card, white)', borderRadius: 14, border: '1px solid var(--border-color, #e5e7eb)',
        padding: 18, marginBottom: 20,
        display: 'flex', flexDirection: 'column',
        minHeight: 'calc(100vh - 120px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexShrink: 0 }}>
          <Clock size={16} style={{ color: '#ec4899' }} />
          <span style={{ fontWeight: 700, fontSize: '.9rem', color: 'var(--text-main)' }}>Turmas</span>
          <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: '.65rem', fontWeight: 600, background: '#ec489915', color: '#ec4899' }}>
            {turmaList.length}
          </span>
        </div>
        {/* Turno legend */}
        {turnoEntries.length > 0 && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', flexShrink: 0 }}>
            {turnoEntries.map(([key, { turno, turmas: tList }]) => {
              const tc = TURNO_COLORS[key] || '#6b7280';
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.68rem', color: 'var(--text-muted)' }}>
                  <div style={{ width: 8, height: 8, borderRadius: 3, background: tc }} />
                  <span style={{ fontWeight: 600 }}>{turno.nome}</span>
                  <span>{turno.horaAberturaPortoes}-{turno.horaSaidaOficial}</span>
                  <span style={{ fontSize: '.6rem', color: tc, fontWeight: 700 }}>({tList.length})</span>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0 }}>
          {turmaList.length === 0 ? (
            <div style={{ fontSize: '.82rem', color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center', padding: 20 }}>Nenhuma turma cadastrada</div>
          ) : (
            renderKanbanColumns(groupBySerie(turmaList))
          )}
        </div>
      </div>

      {/* ═══ DIALOG: ALUNOS DA TURMA ═══ */}
      {selectedTurma && <AlunosDialog turma={selectedTurma} escolaId={id || ''} onClose={() => setSelectedTurma(null)} onImpersonate={handleImpersonate} impersonating={impersonating} onSelectAluno={(al) => setSelectedAluno(al)} />}

      {/* ═══ DIALOG: PERFIL DO USUÁRIO ═══ */}
      {selectedUser && <UserProfileDialog user={selectedUser} turmaList={turmaList} onClose={() => setSelectedUser(null)} onImpersonate={handleImpersonate} impersonating={impersonating} />}

      {/* ═══ DIALOG: PERFIL DO ALUNO ═══ */}
      {selectedAluno && <AlunoProfileDialog aluno={selectedAluno} escolaId={id || ''} onClose={() => setSelectedAluno(null)} onImpersonate={handleImpersonate} impersonating={impersonating} />}
    </div>
  );
}
